import { faMinus } from '@fortawesome/pro-regular-svg-icons/faMinus';
import { faPlus } from '@fortawesome/pro-regular-svg-icons/faPlus';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cn } from '@thinkalpha/ui-core/utils';
import React, { forwardRef, useMemo } from 'react';
import MaskedInput from 'react-currency-input-field';
import { AdornedInputPrimitive, StepperButton, Input } from 'src/components/ui';
import { inputVariants } from 'src/components/ui/input/Input';
import type { MaskedNumberProps } from 'src/components/ui/input/masked/MaskedNumber';
import { useEvent } from 'src/hooks/useEvent';

export const MaskedNumberStepper = forwardRef<
    HTMLInputElement,
    Omit<MaskedNumberProps, 'value'> & {
        step?: number;
        id?: string;
        inputClassName?: string;
        value?: number | string | undefined;
    }
>(
    (
        {
            value,
            onChange,
            defaultValue,
            decimalsLimit = 2,
            step = 0.01,
            disabled,
            className,
            inputClassName,
            allowNegativeValue = true,
            dimensions = 'sm',
            variant = 'default',
            ...props
        },
        ref,
    ) => {
        const val = useMemo(() => value ?? defaultValue ?? 0, [value, defaultValue]);

        const valNumber = useMemo(() => {
            const parsed = Number(val);
            return Number.isFinite(parsed) ? parsed : 0;
        }, [val]);

        const handleDecrement = useEvent(() => {
            const calc = allowNegativeValue ? valNumber - step : Math.max(0, valNumber - step);
            const formatted = calc.toFixed(decimalsLimit);
            onChange(formatted);
        });

        const handleIncrement = useEvent(() => {
            const calc = (valNumber + step).toFixed(decimalsLimit);
            onChange(calc);
        });

        const handleInputKeyDown = useEvent((e: React.KeyboardEvent<HTMLInputElement>) => {
            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault();
                    handleDecrement();
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    handleIncrement();
                    break;
            }
        });

        return (
            <AdornedInputPrimitive.Wrapper
                className={cn('justify-between overflow-clip py-0', className)}
                dimensions={dimensions}
                variant={variant}
                disabled={disabled}
                preAdornment
                postAdornment
            >
                <AdornedInputPrimitive.Adornment className="h-full px-0">
                    <StepperButton
                        onClick={handleDecrement}
                        disabled={disabled}
                        className="h-full"
                        data-testid="dec-button"
                    >
                        <FontAwesomeIcon icon={faMinus} />
                    </StepperButton>
                </AdornedInputPrimitive.Adornment>
                <MaskedInput
                    customInput={Input}
                    ref={ref}
                    value={value?.toString()}
                    defaultValue={defaultValue?.toString()}
                    onValueChange={(value) => {
                        onChange(value ?? '');
                    }}
                    disabled={disabled}
                    allowNegativeValue={allowNegativeValue}
                    decimalsLimit={decimalsLimit}
                    onKeyDown={handleInputKeyDown}
                    className={cn(
                        inputVariants({ variant, dimensions }),
                        'h-full rounded-none border-none py-0 pl-1',
                        inputClassName,
                    )}
                    data-testid="input"
                    {...props}
                />
                <AdornedInputPrimitive.Adornment className="h-full px-0">
                    <StepperButton
                        onClick={handleIncrement}
                        disabled={disabled}
                        className="h-full"
                        data-testid="inc-button"
                    >
                        <FontAwesomeIcon icon={faPlus} />
                    </StepperButton>
                </AdornedInputPrimitive.Adornment>
            </AdornedInputPrimitive.Wrapper>
        );
    },
);

MaskedNumberStepper.displayName = 'MaskedNumberStepper';
